import React, { useEffect, useState } from 'react';
import { mergeStyleSets } from '@fluentui/react';
import ButtonPrimaryRoundedComponent from '../button-primary-rounded/button-primary-rounded.component';
import WorkDocumentCreateComponent from '../work-document/work-document-create.component';

import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemPermiso from '../../models/ItemPermiso';
import ElementEnum from '../../enums/ElementEnum';
import ItemTarea from '../../models/ItemTarea';
import ItemRegistroTiempo from '../../models/ItemRegistroTiempo';

import pauseIcon from '../../assets/img24/pause_24.png';
import WorkPartCreateComponent from '../work-part/work-part-create.component';

const classes = mergeStyleSets({
    workCard: {
        backgroundColor: '#ffffff',
        borderRadius: 10,
        boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
        marginBottom: 10,
        overflow: 'hidden',
    },
    workCardHeader: {
        backgroundColor: 'var(--color-primario)',
        padding: '8px 14px',
    },
    workCardHeaderTitle: {
        color: 'white',
        fontWeight: 600,
        fontSize: 13,
        display: 'block',
    },
    workCardBody: {
        padding: '10px 14px',
    },
    workCardAddress: {
        fontSize: 12,
        color: '#777',
        display: 'block',
        marginBottom: 4,
    },
    workPartRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 0',
        borderTop: '1px solid #f0f0f0',
    },
    workPartIcon: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: 'var(--color-primario)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    workPartContent: {
        flex: 1,
    },
    workPartTime: {
        fontSize: 12,
        fontWeight: 600,
        color: '#333',
    },
    workPartDesc: {
        fontSize: 11,
        color: '#999',
    },
    workPartHours: {
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--color-primario)',
        flexShrink: 0,
    },
    workCardActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 6,
        padding: '8px 14px',
        borderTop: '1px solid #f0f0f0',
    },
    htmlText: ["html-text", {
        fontSize: 12,
        color: '#666',
    }],
});

interface IWorkOrderReadonlyComponentProps {
    workOrder?: ItemOrdenDeTrabajo;
    setLoading: (value: boolean) => void;
};

const WorkOrderReadonlyComponent = (props: IWorkOrderReadonlyComponentProps) => {
    const auth = useAuthStatus();
    const workPermission : ItemPermiso = auth.getPermission(ElementEnum.WORKS);

    const [workOrder, setWorkOrder] = useState<ItemOrdenDeTrabajo | undefined>(props.workOrder);
    const [selectedWork, setSelectedWork] = useState<ItemTarea>();
    const [isWorkDocumentOpen, setIsWorkDocumentOpen] = useState<boolean>(false);
    const [isWorkPartOpen, setIsWorkPartOpen] = useState<boolean>(false);

    useEffect(() => {
        setWorkOrder(props.workOrder);
    }, [props.workOrder]);

    return (
        <>
            {
                workOrder ? (
                    <>
                        {
                            workPermission.Ver ? (
                                workOrder.ListaTareas.map((work: ItemTarea) => (
                                    <div key={work.Id.toString()} className={classes.workCard}>
                                        <div className={classes.workCardHeader}>
                                            <span className={classes.workCardHeaderTitle}>{work.Descripcion}</span>
                                        </div>
                                        <div className={classes.workCardBody}>
                                            {workOrder.Nombre && (
                                                <span className={classes.workCardAddress}>{workOrder.Nombre}</span>
                                            )}
                                            {workOrder.Direccion && (
                                                <span className={classes.workCardAddress}>{workOrder.Direccion}</span>
                                            )}
                                            {work.Observaciones && (
                                                <div
                                                    className={classes.htmlText}
                                                    dangerouslySetInnerHTML={{ __html: work.Observaciones }} />
                                            )}
                                            {workOrder.ListaPartesTrabajo
                                                .filter((workPart: ItemRegistroTiempo) => workPart.IdTarea === work.Id)
                                                .map((workPart: ItemRegistroTiempo) => (
                                                    <div key={workPart.Id.toString()} className={classes.workPartRow}>
                                                        <div className={classes.workPartIcon}>
                                                            <img
                                                                src={pauseIcon}
                                                                width={18}
                                                                height={18}
                                                                alt="Icon" />
                                                        </div>
                                                        <div className={classes.workPartContent}>
                                                            <div className={classes.workPartTime}>
                                                                {workPart.Inicio} — {workPart.Fin}
                                                            </div>
                                                            {workPart.Descripcion && (
                                                                <div className={classes.workPartDesc}>
                                                                    {workPart.Descripcion}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className={classes.workPartHours}>
                                                            {workPart.TiempoEmpleado || 0} h
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        <div className={classes.workCardActions}>
                                            <ButtonPrimaryRoundedComponent
                                                icon="attach"
                                                onClick={() => {
                                                    setSelectedWork(work);
                                                    setIsWorkDocumentOpen(true);
                                                }} />
                                            {workPermission.Nuevo && (
                                                <ButtonPrimaryRoundedComponent
                                                    icon="add"
                                                    onClick={() => {
                                                        setSelectedWork(work);
                                                        setIsWorkPartOpen(true);
                                                    }} />
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                null
                            )
                        }
                        {
                            (isWorkDocumentOpen && selectedWork) ? (
                                <WorkDocumentCreateComponent
                                    work={selectedWork}
                                    onClose={() => setIsWorkDocumentOpen(false)} />
                            ) : (
                                null
                            )
                        }
                        {
                            (workPermission.Nuevo && isWorkPartOpen && selectedWork) ? (
                                <WorkPartCreateComponent
                                    work={selectedWork}
                                    onClose={() => setIsWorkPartOpen(false)} />
                            ) : (
                                null
                            )
                        }
                    </>
                ) : (
                    null
                )
            }
        </>
    );
};

export default WorkOrderReadonlyComponent;
